import React, { useMemo } from "react";
import LineGraph from "../../../components/OrgAdminGraph/LineGraph";
import ChartSkeleton from "../../../components/OrgAdminGraph/ChartSkeleton";
import CRRDashboard from "../../Employee/Dashboard/CRRDashboard";
import { Card, Select } from "../../../components/ds";
import { useSelector } from "react-redux";
import "../../Employee/Dashboard/Dashboard.scss";

const buildProgressSummary = (progressGraphData) => {
  const surveys = Array.isArray(progressGraphData) ? progressGraphData : [];
  if (!surveys.length) {
    return "No maturity progress data yet for this organization.";
  }
  const latest = surveys[0];
  const mils = Array.isArray(latest?.mil_data) ? latest.mil_data : [];
  if (!mils.length) {
    return `Tracking maturity across ${surveys.length} assessment${
      surveys.length === 1 ? "" : "s"
    }.`;
  }
  const avg =
    mils.reduce((sum, d) => sum + (Number(d?.mil) || 0), 0) / mils.length;
  return `${surveys.length} assessment${
    surveys.length === 1 ? "" : "s"
  } · latest average MIL ${avg.toFixed(1)} across ${mils.length} domains.`;
};

const buildChartSummaries = (domains, crrGraphData) => {
  const list = Array.isArray(domains) ? domains : [];
  if (!list.length) {
    return {
      radar: "Select an assessment to load domain coverage.",
      mil: "MIL results appear once assessment data is available.",
      performance: "Domain performance summary loads with the selected survey.",
    };
  }

  const complete = list.filter(
    (d) => Number(d.percentage_completed) >= 100
  ).length;
  const avgPct = Math.round(
    list.reduce((sum, d) => sum + (Number(d.percentage_completed) || 0), 0) /
      list.length
  );

  const milRows = Array.isArray(crrGraphData?.[0]?.mil_data)
    ? crrGraphData[0].mil_data
    : [];
  const milHint = milRows.length
    ? `${milRows.length} maturity-level series for the selected survey.`
    : "MIL profile updates as assessment results come in.";

  return {
    radar: `${complete} of ${list.length} domains at 100% · average progress ${avgPct}%.`,
    mil: milHint,
    performance: `Response mix across ${list.length} domains for the selected assessment.`,
  };
};

const DashboardGraph = ({ handleFilterChange, selectedSurvey, isAdmin }) => {
  const {
    loading,
    employeeDashboardLoading,
    employeeDashboardData,
    milMasterData,
    crrGraphData,
    progressGraphData,
    allSurveysList,
  } = useSelector((state) => state.employeeDashboard);

  const surveyOptions = useMemo(
    () =>
      (allSurveysList || []).map((filter) => ({
        value: filter?.id,
        label: filter?.name,
      })),
    [allSurveysList]
  );

  const progressSummary = useMemo(
    () => buildProgressSummary(progressGraphData),
    [progressGraphData]
  );

  const chartSummaries = useMemo(
    () =>
      buildChartSummaries(employeeDashboardData?.domains, crrGraphData),
    [employeeDashboardData, crrGraphData]
  );

  return (
    <div
      className={
        isAdmin ? "admin-dashboard-graphs" : "employee-dashboard-graphs"
      }
    >
      <Card
        className="dashboard-graph-container"
        role="region"
        aria-label="Maturity progress"
      >
        <div className="progress-card-head">
          <p className="progress-card-eyebrow">Trendline</p>
          <h2 className="progress-card-label">Maturity progress</h2>
        </div>
        <div className="progress-graph">
          {loading ? (
            <ChartSkeleton variant="line" />
          ) : (
            <LineGraph graphData={progressGraphData} chartHeight={320} compact />
          )}
        </div>
        <p className="chart-summary">{progressSummary}</p>
      </Card>

      <div className="admin-dashboard-filter">
        {surveyOptions.length > 0 && (
          <Select
            size="md"
            value={selectedSurvey == null ? "" : String(selectedSurvey)}
            onChange={handleFilterChange}
            options={surveyOptions.map((o) => ({
              ...o,
              value: o.value == null ? "" : String(o.value),
            }))}
            aria-label="Select assessment"
          />
        )}
      </div>

      <section
        className="admin-dashboard-analytics"
        aria-label="Assessment analytics"
      >
        <div className="dashboard-charts-header">
          <p className="domain-ribbon-eyebrow">Analytics</p>
          <h2 className="domain-ribbon-title">Assessment instruments</h2>
        </div>
        <CRRDashboard
          employeeDashboardData={employeeDashboardData}
          crrGraphData={crrGraphData}
          milMasterData={milMasterData}
          selectedSurvey={selectedSurvey}
          isAdmin={isAdmin}
          loading={employeeDashboardLoading}
          cinematic
          chartSummaries={chartSummaries}
        />
      </section>
    </div>
  );
};

export default DashboardGraph;
